import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

/**
 * Subscription Context Provider
 * Manages user subscription status and plans
 */
export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);

  // Load subscription from localStorage
  useEffect(() => {
    if (user) {
      const storedSubscription = localStorage.getItem(`rocketverse_subscription_${user.id}`);
      if (storedSubscription) {
        setSubscription(JSON.parse(storedSubscription));
      }
    } else {
      setSubscription(null);
    }
  }, [user]);

  /**
   * Subscription plans
   */
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      features: [
        'View launches',
        'Browse rockets and agencies',
        'Read articles and news'
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 9.99,
      features: [
        'All Free features',
        'Post articles',
        'Access chat rooms',
        'Advanced launch projections',
        'Save favorites'
      ]
    }
  ];

  /**
   * Purchase subscription
   */
  const purchaseSubscription = (planId) => {
    if (!user) {
      return { success: false, message: 'Please login first' };
    }

    if (planId === 'free') {
      setSubscription({
        planId: 'free',
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: null
      });
      localStorage.setItem(`rocketverse_subscription_${user.id}`, JSON.stringify({
        planId: 'free',
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: null
      }));
      return { success: true, message: 'Free plan activated' };
    }

    // Mock payment processing
    const newSubscription = {
      planId: planId,
      status: 'active',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
    };

    setSubscription(newSubscription);
    localStorage.setItem(`rocketverse_subscription_${user.id}`, JSON.stringify(newSubscription));
    return { success: true, message: 'Subscription purchased successfully' };
  };

  /**
   * Check if user is subscribed
   */
  const isSubscribed = () => {
    return subscription?.status === 'active' && subscription?.planId === 'premium';
  };

  /**
   * Check if user can perform premium action
   */
  const canAccessPremium = () => {
    if (!user) return false;
    return isSubscribed() || user.role === 'admin';
  };

  const value = {
    subscription,
    plans,
    purchaseSubscription,
    isSubscribed,
    canAccessPremium
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Custom hook to use Subscription Context
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
